import {
  type AuthClient,
  type GetAccessTokenOptions,
} from '@/features/authentication/utils/authentication/client'
import {
  _retrieveAccessToken,
  _retrieveAuthData,
} from '@/features/authentication/utils/authentication/client/auth0/service'
import { getIsTokenExpired } from '@/features/authentication/utils/authentication/client/auth0/utils'
import { resetUser } from '@/features/authentication/utils/authentication'
import { safeAsyncDebounce } from '@/features/authentication/utils/authentication/utils'
import { logError } from '@/features/shared/utils/logger'
import { getCookie } from '@/features/shared/utils/cookies'
import { hasNewAuthSessionCookieName } from '@shared/constants/auth'

const _clientSideGetAccessToken = safeAsyncDebounce(async function _getAccessToken({
  forceRefresh,
}: GetAccessTokenOptions = {}) {
  return _retrieveAccessToken({ forceRefresh }).catch((err) => {
    // If the refresh auth call failed we CANNOT continue.

    if (getCookie(hasNewAuthSessionCookieName) !== true) {
      // unauthenticated sessions have no access token; return undefined, early
      return undefined
    }

    logError(new Error('Error fetching or refreshing auth token', { cause: err }), {
      customGroupingHash: 'auth0-data-error',
    })

    // Otherwise there is an auth session, and we must clear any auth data and logout.
    resetUser()
    throw err
  })
})

const _getAccessToken = async ({ forceRefresh = false }: GetAccessTokenOptions = {}) => {
  // get access token on the client only for users with an active session
  if (getCookie(hasNewAuthSessionCookieName) === true) {
    return _clientSideGetAccessToken({ forceRefresh })
  }
}

const _getIsTokenExpired = async () => {
  try {
    const { expiresAt } = await _retrieveAuthData()
    return getIsTokenExpired(expiresAt)
  } catch {
    return false
  }
}

export const _createClient = (): AuthClient => {
  return {
    getAccessToken: _getAccessToken,
    getIsTokenExpired: _getIsTokenExpired,
  }
}
